<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoiceReferenceSearch',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoiceId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onReferenceSelected = (...args) => {
    const { option, value } = args[0];
    if (option) {
      selectedItems.value.push(option);

      if (option.rental_id && option.rental_name) {
        selectedItems.value.push({
          object: 'rental',
          id: option.rental_id,
          name: option.rental_name,
        });
      }

      if (option.client_id && option.client_name) {
        selectedItems.value.push({
          object: 'company',
          id: option.client_id,
          name: option.client_name,
        });
      }
      if (option.properties) {
        _.each(option.properties, (p) => {
          if (p.object == 'rental_property' && !p.inactive && p.property_unit) {
            selectedItems.value.push({
              object: 'property_unit',
              id: p.property_unit.id,
              name: p.property_unit.full_name || p.property_unit.full_code,
            });
          }
        });
      }
    }
  };

  const formId = ref(null);
  const searchTerm = ref(null);
  const items = ref([]);
  const selectedItems = ref([]);
  const referenceTypes = ref(null);
  const selectedReferenceType = ref(null);
  const selectedReference = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_7789867021953041_clearable = computed(() => false);

  const $data = computed(() => ({
    formId: formId.value,
    searchTerm: searchTerm.value,
    items: items.value,
    selectedItems: selectedItems.value,
    referenceTypes: referenceTypes.value,
    selectedReferenceType: selectedReferenceType.value,
    selectedReference: selectedReference.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7789867021953041_clearable: b_7789867021953041_clearable.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_referenceTypes = () => {
    return [
      {
        type: 'contract',
        text: $fn.value.gettext('Contracts'),
        target: 'contracts',
        dataSource: {
          name: 'suggest_contract',
          parameters: {},
        },
        template: '',
      },
      {
        type: 'rental',
        text: $fn.value.gettext('Rentals'),
        target: 'rentals',
        dataSource: {
          name: 'suggest_rental',
          parameters: {},
        },
      },
      {
        type: 'client',
        text: $fn.value.gettext('Clients'),
        target: 'tenants',
        dataSource: {
          name: 'suggest_company',
          parameters: {
            role: 'partner',
          },
        },
      },
      {
        type: 'property',
        text: $fn.value.gettext('Properties'),
        target: 'properties',
        dataSource: {
          name: 'suggest_property',
          parameters: {
            types: 'property,unit',
          },
        },
      },
    ];
  };
  const $$load_selectedReference = () => {
    return (
      _.find(referenceTypes.value, { type: selectedReferenceType.value }) ||
      referenceTypes.value[0]
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'searchTerm' },
      { name: 'items' },
      { name: 'selectedItems' },
      { name: 'referenceTypes' },
      {
        name: 'selectedReferenceType',
        cache: {
          type: 'local',
          key: 'InvoiceReferenceSearchModal.selectedReferenceType',
        },
      },
      { name: 'selectedReference', dependsOn: ['referenceTypes'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7789867021953041_clearable', nodeId: '7789867021953041' },
    ],
    loaders: {
      formId: $$load_formId,
      referenceTypes: $$load_referenceTypes,
      selectedReference: $$load_selectedReference,
    },
    ux: {},
    dataRefs: {
      formId,
      searchTerm,
      items,
      selectedItems,
      referenceTypes,
      selectedReferenceType,
      selectedReference,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7789867021953041_clearable,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Invoice', 'Search Reference') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoiceId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoiceId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_2fwgclmxh_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_7789867021953041_setData_qxtd2nx9o_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'searchTerm',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_setData_ifkxmzz33_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'items',
      value: [],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_setData_ofsxe55ux_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedReferenceType',
      value: $event.data.value.type,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3468003412020058_script_bdmpki09h_value_callable = async (
    $event
  ) => {
    return onReferenceSelected($event.data);
  };
  const act_3468003412020058_script_bdmpki09h_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3468003412020058_script_bdmpki09h_value_callable($event);
      },
    };
  };
  const act_3468003412020058_setData_7w9aqy1ig_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'searchTerm',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3468003412020058_setData_hmnwxo4gv_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'items',
      value: [],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6049458597007392_setData_880nuqywk_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value:
        (_.remove(selectedItems.value, $event.scope.item),
        selectedItems.value.slice()),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2140198529698299_closeComponent_b22tiaz7t_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_k6yaq7ao8_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice_reference',
      data: _.map(selectedItems.value, (p) => ({ reference_id: p.id })),
      parameters: {
        invoice_id: props.invoiceId,
      },
    };
  };

  const act_7315092382398562_closeModal_2fwgclmxh =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_2fwgclmxh_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ux0talyox =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_2fwgclmxh,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ux0talyox],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_50619l20s =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_qqkfawwxn =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_50619l20s,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_qqkfawwxn],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_k3ujnl321 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_929eh8461 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_k3ujnl321,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_929eh8461],
    event: 'close',
  });
  const act_7789867021953041_setData_qxtd2nx9o = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_qxtd2nx9o_getActionArgs,
  });
  const inst_evh_7789867021953041_optionSelected_e6ivdpimc =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_qxtd2nx9o,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_7789867021953041_setData_ifkxmzz33 = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_ifkxmzz33_getActionArgs,
  });
  const inst_evh_7789867021953041_optionSelected_f71ysdu94 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_ifkxmzz33,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_7789867021953041_setData_ofsxe55ux = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_ofsxe55ux_getActionArgs,
  });
  const inst_evh_7789867021953041_optionSelected_w8vwb3db0 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_ofsxe55ux,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_7789867021953041_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_7789867021953041_optionSelected_e6ivdpimc,
        inst_evh_7789867021953041_optionSelected_f71ysdu94,
        inst_evh_7789867021953041_optionSelected_w8vwb3db0,
      ],
      event: 'optionSelected',
    });
  const act_3468003412020058_script_bdmpki09h = new actionTypes.ScriptAction({
    actionArgs: act_3468003412020058_script_bdmpki09h_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_zo6oveqal =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_script_bdmpki09h,
      event: 'optionSelected',
      displayName: 'script',
    });
  const act_3468003412020058_setData_7w9aqy1ig = new actionTypes.SetDataAction({
    actionArgs: act_3468003412020058_setData_7w9aqy1ig_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_cvcgh8m48 =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_setData_7w9aqy1ig,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_3468003412020058_setData_hmnwxo4gv = new actionTypes.SetDataAction({
    actionArgs: act_3468003412020058_setData_hmnwxo4gv_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_dneq3wsv8 =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_setData_hmnwxo4gv,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_3468003412020058_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_3468003412020058_optionSelected_zo6oveqal,
        inst_evh_3468003412020058_optionSelected_cvcgh8m48,
        inst_evh_3468003412020058_optionSelected_dneq3wsv8,
      ],
      event: 'optionSelected',
    });
  const act_6049458597007392_setData_880nuqywk = new actionTypes.SetDataAction({
    actionArgs: act_6049458597007392_setData_880nuqywk_getActionArgs,
  });
  const inst_evh_6049458597007392_clickPreventStop_i0degscz5 =
    new actionTypes.EventHandlerImpl({
      action: act_6049458597007392_setData_880nuqywk,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_6049458597007392_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6049458597007392_clickPreventStop_i0degscz5],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_closeComponent_b22tiaz7t =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2140198529698299_closeComponent_b22tiaz7t_getActionArgs,
    });
  const inst_evh_2140198529698299_success_vjt8p5yp7 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_b22tiaz7t,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2140198529698299_request_k6yaq7ao8 = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_k6yaq7ao8_getActionArgs,
    displayName: 'submitReferences',
    events: [inst_evh_2140198529698299_success_vjt8p5yp7],
  });
  const inst_evh_2140198529698299_clickPreventStop_6pswytxxi =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_k6yaq7ao8,
      event: 'click.prevent.stop',
      displayName: 'submitReferences',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_6pswytxxi],
      event: 'click.prevent.stop',
    });
  const act_2434514588044777_closeComponent_hyox5vhk2 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_fkex4gm8q =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_hyox5vhk2,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_fkex4gm8q],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ux0talyox,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_qqkfawwxn,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_929eh8461,
    inst_evh_8564662037462133_close,
    inst_evh_7789867021953041_optionSelected_e6ivdpimc,
    inst_evh_7789867021953041_optionSelected_f71ysdu94,
    inst_evh_7789867021953041_optionSelected_w8vwb3db0,
    inst_evh_7789867021953041_optionSelected,
    inst_evh_3468003412020058_optionSelected_zo6oveqal,
    inst_evh_3468003412020058_optionSelected_cvcgh8m48,
    inst_evh_3468003412020058_optionSelected_dneq3wsv8,
    inst_evh_3468003412020058_optionSelected,
    inst_evh_6049458597007392_clickPreventStop_i0degscz5,
    inst_evh_6049458597007392_clickPreventStop,
    inst_evh_2140198529698299_success_vjt8p5yp7,
    inst_evh_2140198529698299_clickPreventStop_6pswytxxi,
    submitReferences: inst_evh_2140198529698299_clickPreventStop_6pswytxxi,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2434514588044777_clickPreventStop_fkex4gm8q,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_ux0talyox = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ux0talyox.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_qqkfawwxn = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_qqkfawwxn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_929eh8461 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_929eh8461.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected_e6ivdpimc = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_optionSelected_e6ivdpimc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected_f71ysdu94 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_optionSelected_f71ysdu94.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected_w8vwb3db0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_optionSelected_w8vwb3db0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_zo6oveqal = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_zo6oveqal.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_cvcgh8m48 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_cvcgh8m48.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_dneq3wsv8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_dneq3wsv8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected = async (event, scope) => {
    return await actions.inst_evh_3468003412020058_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6049458597007392_clickPreventStop_i0degscz5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6049458597007392_clickPreventStop_i0degscz5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6049458597007392_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6049458597007392_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_vjt8p5yp7 = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_vjt8p5yp7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_6pswytxxi = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_6pswytxxi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_fkex4gm8q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_fkex4gm8q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoiceReferenceSearch"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm name="search" :id="formId">
        <div class="row">
          <ItpFormSearchableSelect
            name="selectedReference"
            v-model="selectedReference"
            :options="referenceTypes"
            valueField="target"
            textField="text"
            :clearable="b_7789867021953041_clearable"
            bind-object
            class="col-4"
            @optionSelected="evh_7789867021953041_optionSelected($event, {})"
          >
          </ItpFormSearchableSelect>
          <ItpFormSearchableSelect
            name="search"
            v-model="searchTerm"
            :placeholder="pgettext('Search Placeholder', 'Search...')"
            :dataSource="selectedReference && selectedReference.dataSource"
            :options="items"
            valueField="id"
            bind-object
            optionTemplate="<InvoiceReferenceSearchOption :option=option>
</InvoiceReferenceSearchOption>"
            use-option-template-for-selected-option
            class="col-8"
            @optionSelected="evh_3468003412020058_optionSelected($event, {})"
          >
          </ItpFormSearchableSelect>
        </div>
        <BListGroup class="mt-3">
          <BListGroupItem :key="item.id" v-for="item in selectedItems">
            <ItpBox alignItems="start">
              <InvoiceReferenceItem :item="item" no-link>
              </InvoiceReferenceItem>
              <button
                :aria-label="pgettext('InvoiceReferenceSearch', 'Remove')"
                type="button"
                class="btn-close ms-auto me-1"
                @click.prevent.stop="
                  evh_6049458597007392_clickPreventStop($event, { item })
                "
              ></button>
            </ItpBox>
          </BListGroupItem>
        </BListGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('InvoiceReferenceSearch/Submit', 'Add references')"
          variant="primary"
          icon="fa-save"
          :spinning="
            actions.submitReferences && actions.submitReferences.isRunning
          "
          :disabled="!selectedItems.length"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="
            (actions.search && actions.search.isRunning) ||
            (actions.submitReferences && actions.submitReferences.isRunning)
          "
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
